import { lazyRouteComponent } from "@tanstack/react-router";
import { Binoculars } from "lucide-react";

import type { PageDef } from "@/app/registry";

export const FEED_KINDS = ["all", "deals", "insider", "sast", "pledge", "fii_dii"] as const;
export type FeedKindParam = (typeof FEED_KINDS)[number];
export const FEED_DAYS = [7, 30, 90, 365] as const;
export const WHO = ["all", "promoter", "big_investor"] as const;
export type WhoParam = (typeof WHO)[number];
export const ACTIONS = ["any", "buy", "sell", "pledge"] as const;
export type ActionParam = (typeof ACTIONS)[number];

export interface SmartMoneySearch {
  /** Which kind of event the feed shows (default all). */
  kind: FeedKindParam;
  /** Feed window in days (default 30). */
  days: number;
  who: WhoParam;
  action: ActionParam;
  /** Show same-day in-and-out traders in the deals. */
  rt: boolean;
}

const pick = <T extends string>(list: readonly T[], v: unknown, fallback: T): T =>
  list.includes(v as T) ? (v as T) : fallback;

export function parseSmartMoneySearch(s: Record<string, unknown>): SmartMoneySearch {
  const days = Number(s.days);
  return {
    kind: pick(FEED_KINDS, s.kind, "all"),
    days: (FEED_DAYS as readonly number[]).includes(days) ? days : 30,
    who: pick(WHO, s.who, "all"),
    action: pick(ACTIONS, s.action, "any"),
    rt: s.rt === true || s.rt === "1" || s.rt === 1,
  };
}

export const STOCK_DAYS = [90, 365, 1095] as const;

export interface StockSmartMoneySearch {
  /** Timeline window in days (default a year). */
  days: number;
  /** Which kind of event the timeline shows (default all). */
  show: "all" | "deals" | "insider" | "sast" | "pledge" | "shareholding";
}

export function parseStockSearch(s: Record<string, unknown>): StockSmartMoneySearch {
  const days = Number(s.days);
  const show = ["all", "deals", "insider", "sast", "pledge", "shareholding"] as const;
  return {
    days: (STOCK_DAYS as readonly number[]).includes(days) ? days : 365,
    show: pick(show, s.show, "all"),
  };
}

export const pages: PageDef[] = [
  {
    path: "/app/smart-money",
    proOnly: true,
    title: "Smart Money Tracker",
    component: lazyRouteComponent(() => import("./SmartMoneyPage"), "SmartMoneyPage"),
    nav: { label: "Smart Money Tracker", icon: Binoculars, group: "Premium", order: 9 },
    validateSearch: parseSmartMoneySearch,
  },
  {
    path: "/app/smart-money/$symbol",
    proOnly: true,
    title: "Smart money",
    component: lazyRouteComponent(() => import("./StockSmartMoneyPage"), "StockSmartMoneyPage"),
    validateSearch: parseStockSearch,
  },
];
